import { useState, useEffect } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { ClockIcon } from "@heroicons/react/24/outline";
import { Switch } from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { useMutation } from "@tanstack/react-query";
import { createSubscription } from "../api/mutations";
import { useNavigate } from "react-router-dom";
import { useStore } from "../contexts/StoreContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchBillingPortalUrl } from "../api/queries";
import { useQueryClient } from "@tanstack/react-query";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const tiers = [
  {
    name: "starter",
    id: import.meta.env.VITE_STARTER_PLAN_ID,
    featured: false,
    soon: [],
  },
  {
    name: "growth",
    id: import.meta.env.VITE_GROWTH_PLAN_ID,
    featured: true,
    soon: [],
  },
  {
    name: "advanced",
    id: import.meta.env.VITE_ADVANCED_PLAN_ID,
    featured: false,
    soon: [4],
  },
];

const tierOrder = ["starter", "growth", "advanced"];

export default function Pricing({ setupMode = false }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { selectedStore } = useStore();
  const [annual, setAnnual] = useState(selectedStore.billing_cycle != "month");

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createSubscriptionMutation = useMutation({
    mutationFn: createSubscription,
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    },
  });

  useEffect(() => {
    setAnnual(selectedStore.billing_cycle != "month");
  }, [selectedStore]);

  const handleBillingPortal = async () => {
    try {
      const response = await queryClient.fetchQuery({
        queryKey: ["billingPortal", selectedStore.id],
        queryFn: () => fetchBillingPortalUrl(selectedStore.id),
      });

      window.location.href = response.portal_url;
    } catch (error) {
      console.error("Error accessing billing portal:", error);
    }
  };

  const handleSubscribe = async (tierId) => {
    if (!user) {
      // Redirect to login if user is not authenticated
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }

    if (tierId === "enterprise") {
      window.location.href = "https://loyaltify.io/demo";
      return;
    }

    if (
      selectedStore?.subscription_status === "active" ||
      selectedStore?.subscription_status === "past_due" ||
      selectedStore?.subscription_status === "trialing"
    ) {
      handleBillingPortal();
      return;
    }

    try {
      await createSubscriptionMutation.mutateAsync({
        productId: tierId,
        interval: annual ? "year" : "month",
        storeId: selectedStore.id,
      });
    } catch (error) {
      console.error("Error creating subscription:", error);
    }
  };

  return (
    <div className="mx-auto max-w-8xl">
      <>
        <div className={`mx-auto max-w-4xl text-center`}>
          <h2 className="text-base font-semibold leading-7 text-indigo-600">
            {t("pricing.title")}
          </h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            {t("pricing.description")}
          </p>
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
          {t("pricing.subdescription")}
        </p>
      </>
      {/* Toggle annual/monthly pricing */}
      <div className="mt-16 flex justify-center">
        <div className="relative flex items-center gap-x-4">
          <span
            className={classNames(
              annual ? "text-gray-500" : "text-gray-900",
              "text-sm font-semibold",
            )}
          >
            {t("pricing.monthly")}
          </span>
          <Switch
            checked={annual}
            onChange={setAnnual}
            className={classNames(
              annual ? "bg-indigo-600" : "bg-gray-200",
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
            )}
          >
            <span
              className={classNames(
                annual ? "translate-x-5" : "translate-x-0",
                "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out",
              )}
            >
              <span
                className={classNames(
                  annual
                    ? "opacity-0 duration-100 ease-out"
                    : "opacity-100 duration-200 ease-in",
                  "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                )}
                aria-hidden="true"
              >
                <svg
                  className="h-3 w-3 text-gray-400"
                  fill="none"
                  viewBox="0 0 12 12"
                >
                  <path
                    d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span
                className={classNames(
                  annual
                    ? "opacity-100 duration-200 ease-in"
                    : "opacity-0 duration-100 ease-out",
                  "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                )}
                aria-hidden="true"
              >
                <svg
                  className="h-3 w-3 text-indigo-600"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                >
                  <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                </svg>
              </span>
            </span>
          </Switch>
          <span
            className={classNames(
              !annual ? "text-gray-500" : "text-gray-900",
              "text-sm font-semibold",
            )}
          >
            {t("pricing.annual")}
            <span className="text-green-500 ml-1.5">
              ({t("pricing.annualDiscount")})
            </span>
          </span>
        </div>
      </div>

      {/* Pricing cards */}
      <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-6 lg:gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={classNames(
              (selectedStore.current_plan == null) & tier.featured ||
                selectedStore.current_plan == tier.name
                ? "ring-2 ring-indigo-600"
                : "ring-1 ring-gray-200",
              "rounded-3xl p-6 lg:p-8 bg-white shadow-xs hover:shadow-md transition-shadow",
            )}
          >
            <div className="flex items-center justify-between gap-x-4">
              <h3
                className={classNames(
                  (selectedStore.current_plan == null) & tier.featured ||
                    selectedStore.current_plan == tier.name
                    ? "text-indigo-600"
                    : "text-gray-900",
                  "text-xl font-semibold leading-8",
                )}
              >
                {t("pricing.tier." + tier.name + ".name")}
              </h3>
              {selectedStore?.current_plan == tier.name ? (
                <p className="rounded-full bg-indigo-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600">
                  {t("pricing.currentPlan")}
                </p>
              ) : (
                tier.featured &&
                selectedStore?.current_plan == null && (
                  <p className="rounded-full bg-indigo-600/10 px-2.5 py-1 text-xs font-semibold leading-5 text-indigo-600">
                    {t("pricing.mostPopular")}
                  </p>
                )
              )}
            </div>
            <p className="mt-4 text-md h-16 leading-6 text-gray-600">
              {t("pricing.tier." + tier.name + ".description")}
            </p>
            <p className="mt-6 flex items-baseline gap-x-1">
              <span className="text-4xl font-bold tracking-tight text-gray-900">
                €
                {annual
                  ? t("pricing.tier." + tier.name + ".priceYearly")
                  : t("pricing.tier." + tier.name + ".priceMonthly")}
              </span>
              <span className="text-sm font-semibold leading-6 text-gray-600 ">
                / {annual ? t("pricing.year") : t("pricing.month")}
              </span>
            </p>
            <button
              onClick={() => handleSubscribe(tier.id)}
              className={classNames(
                (selectedStore.current_plan == null) & tier.featured ||
                  selectedStore.current_plan == tier.name
                  ? "bg-indigo-600 text-white shadow-xs hover:bg-indigo-500"
                  : "bg-white text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-500 hover:text-white",
                "mt-6 block w-full rounded-md px-3 py-2 text-center text-sm font-semibold leading-6",
                "focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                "transition-colors ease-in-out duration-300",
              )}
            >
              {selectedStore?.subscription_status === "active" ||
              selectedStore?.subscription_status === "past_due" ||
              selectedStore?.subscription_status === "trialing"
                ? selectedStore?.current_plan == tier.name
                  ? t("pricing.manageSubscription")
                  : tierOrder.indexOf(selectedStore?.current_plan) >
                      tierOrder.indexOf(tier.name)
                    ? t("pricing.downgrade")
                    : t("pricing.upgrade")
                : setupMode
                  ? t("pricing.startFreeTrial")
                  : t("pricing.subscribe")}
            </button>
            <ul
              role="list"
              className="mt-8 space-y-3 text-sm leading-6 text-gray-600"
            >
              {Object.values(t("pricing.tier." + tier.name + ".features")).map(
                (feature, index) => (
                  <li key={index} className="flex gap-x-3">
                    {tier.soon.includes(index) ? (
                      <ClockIcon
                        className="h-6 w-5 flex-none text-gray-400"
                        aria-hidden="true"
                      />
                    ) : (
                      <CheckIcon
                        className="h-6 w-5 flex-none text-indigo-600"
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className={
                        tier.soon.includes(index) ? "text-gray-400" : ""
                      }
                    >
                      {feature}
                    </span>
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
        {/* Enterprise */}
        <div
          key="enterprise"
          className="lg:col-span-3 ring-1 bg-gray-900 text-white ring-gray-200 rounded-3xl p-6 lg:p-8 lg:text-center"
        >
          <h3 className="text-gray-100 text-xl font-semibold leading-8">
            {t("pricing.tier.enterprise.name")}
          </h3>
          <p className="mt-4 text-base leading-6 text-gray-300">
            {t("pricing.tier.enterprise.description")}
          </p>
          <button
            onClick={() => handleSubscribe("enterprise")}
            className={classNames(
              "text-gray-100 ring-1 ring-inset ring-indigo-200 max-w-md lg:max-w-sm bg-gray-800",
              "mt-6 block w-full lg:inline-block lg:w-none rounded-md px-3 py-2 text-center text-sm font-semibold leading-6",
              "transition-colors ease-in-out duration-300 hover:bg-indigo-600",
            )}
          >
            {t("pricing.contactUs")}
          </button>
        </div>
      </div>
    </div>
  );
}
